import React, { createContext, useContext, useEffect, useRef, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import {
  connectNotifications,
  disconnectNotifications,
  onChangeEvent,
  ChangeEvent,
} from '../services/notificationService';

type EventCallback = (event: ChangeEvent) => void;

interface NotificationContextType {
  subscribe: (eventType: string, callback: EventCallback) => () => void;
  subscribeToAlbum: (albumId: string, callback: EventCallback) => () => void;
}

const defaultContextValue: NotificationContextType = {
  subscribe: () => () => {},
  subscribeToAlbum: () => () => {},
};

const NotificationContext = createContext<NotificationContextType>(defaultContextValue);

export const useNotification = (): NotificationContextType => {
  return useContext(NotificationContext);
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();

  // Map of eventType -> Set<callback>
  const typeSubscribersRef = useRef<Map<string, Set<EventCallback>>>(new Map());
  // Map of albumId -> Set<callback>
  const albumSubscribersRef = useRef<Map<string, Set<EventCallback>>>(new Map());

  // Central event handler that routes events to matching subscribers
  useEffect(() => {
    if (!isAuthenticated) return;

    const unsubscribe = onChangeEvent((event: ChangeEvent) => {
      // Notify type-based subscribers
      const typeSubs = typeSubscribersRef.current.get(event.type);
      if (typeSubs) {
        typeSubs.forEach(cb => { try { cb(event); } catch { /* ignore */ } });
      }

      // Notify album-based subscribers
      if (event.albumId) {
        const albumSubs = albumSubscribersRef.current.get(event.albumId);
        if (albumSubs) {
          albumSubs.forEach(cb => { try { cb(event); } catch { /* ignore */ } });
        }
      }
    });

    return unsubscribe;
  }, [isAuthenticated]);

  // Connect/disconnect SSE based on auth state
  useEffect(() => {
    if (isAuthenticated) {
      connectNotifications();
    } else {
      disconnectNotifications();
    }
    return () => disconnectNotifications();
  }, [isAuthenticated]);

  const subscribe = useCallback((eventType: string, callback: EventCallback): (() => void) => {
    const subs = typeSubscribersRef.current;
    if (!subs.has(eventType)) {
      subs.set(eventType, new Set());
    }
    subs.get(eventType)!.add(callback);

    return () => {
      const set = subs.get(eventType);
      if (set) {
        set.delete(callback);
        if (set.size === 0) subs.delete(eventType);
      }
    };
  }, []);

  const subscribeToAlbum = useCallback((albumId: string, callback: EventCallback): (() => void) => {
    const subs = albumSubscribersRef.current;
    if (!subs.has(albumId)) {
      subs.set(albumId, new Set());
    }
    subs.get(albumId)!.add(callback);

    return () => {
      const set = subs.get(albumId);
      if (set) {
        set.delete(callback);
        if (set.size === 0) subs.delete(albumId);
      }
    };
  }, []);

  const value: NotificationContextType = {
    subscribe,
    subscribeToAlbum,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
